/**
 * Utility: Set a value at a dot-path in an object
 * Example: setValueAtPath(obj, "data.message", "Hello") sets obj.data.message = "Hello"
 */
const reservedKey = (key) => ['__proto__', 'prototype', 'constructor'].includes(key);

// Throws when the path cannot be set: the caller refuses the whole patch then,
// as it always did, rather than applying the rest of it. The message names the
// node and path, because it is fed back to the LLM to correct its answer.
function setValueAtPath(obj, path, value, nodeId) {
  const keys = path.split('.');
  if (keys.some(reservedKey)) {
    throw new Error(`Reserved field path in patch: ${nodeId}.${path}`);
  }
  let current = obj;

  for (let i = 0; i < keys.length - 1; i++) {
    const key = keys[i];
    // Do not traverse inherited objects/functions (for example toString).
    // A JSON patch may only walk its own JSON data, never shared built-ins.
    const own = Object.prototype.hasOwnProperty.call(current, key);
    if (!own && !(key in current)) current[key] = {};
    if (!Object.prototype.hasOwnProperty.call(current, key) || !current[key] || typeof current[key] !== 'object') {
      throw new Error(`Cannot set ${nodeId}.${path}: ${keys.slice(0, i + 1).join('.')} is not an object`);
    }
    current = current[key];
  }

  current[keys[keys.length - 1]] = value;
}

/**
 * Utility: Remove a node and clean up all references to it in other nodes
 */
function removeNodeAndCleanReferences(scenario, nodeIdToRemove) {
  // Remove the node itself
  delete scenario[nodeIdToRemove];

  // Clean up references in other nodes
  Object.values(scenario).forEach(node => {
    // Skip null or undefined nodes
    if (!node || typeof node !== 'object') {
      return;
    }

    if (node.nextNodes) {
      node.nextNodes = node.nextNodes.filter(id => id !== nodeIdToRemove);
    }
    if (node.prevNodes) {
      node.prevNodes = node.prevNodes.filter(id => id !== nodeIdToRemove);
    }
  });
}

/**
 * Utility: Apply patches to a scenario and return the modified scenario
 * @param {Object} scenario - The current scenario object
 * @param {Object} patches - The patches object with add/update/remove operations
 * @returns {Object} - The modified scenario (new object, does not mutate original)
 */
export function applyPatchesToScenario(scenario, patches) {
  // Validate inputs
  if (!scenario || typeof scenario !== 'object') {
    console.error('Invalid scenario provided to applyPatchesToScenario:', scenario);
    return {
      success: false,
      scenario: scenario || {},
      changes: {
        added: [],
        updated: [],
        removed: [],
        errors: ['Invalid or null scenario provided'],
      },
    };
  }

  if (!patches || typeof patches !== 'object') {
    console.warn('No valid patches provided');
    return {
      success: true,
      scenario,
      changes: {
        added: [],
        updated: [],
        removed: [],
        errors: [],
      },
    };
  }

  // Deep clone the scenario to avoid mutating the original
  const modifiedScenario = JSON.parse(JSON.stringify(scenario));

  // Track changes for logging
  const changes = {
    added: [],
    updated: [],
    removed: [],
    errors: [],
    warnings: [], // Non-critical issues (e.g., trying to update already-removed nodes)
  };

  // 1. Handle removals first (to avoid updating nodes that will be deleted)
  if (patches.remove && Array.isArray(patches.remove)) {
    patches.remove.forEach(nodeId => {
      if (reservedKey(nodeId)) {
        changes.errors.push('Reserved node identity in patch');
        return;
      }
      if (modifiedScenario[nodeId]) {
        removeNodeAndCleanReferences(modifiedScenario, nodeId);
        changes.removed.push(nodeId);
        console.log(`✅ Removed node: ${nodeId}`);
      } else {
        const error = `Node ${nodeId} not found (cannot remove)`;
        changes.errors.push(error);
        console.warn(`⚠️ ${error}`);
      }
    });
  }

  // 2. Handle additions
  if (patches.add && typeof patches.add === 'object') {
    Object.entries(patches.add).forEach(([nodeId, nodeData]) => {
      if (reservedKey(nodeId)) {
        changes.errors.push('Reserved node identity in patch');
        return;
      }
      // Check if node exists and is not null
      const existingNode = modifiedScenario[nodeId];
      if (existingNode !== undefined && existingNode !== null) {
        // Replace the existing node instead of erroring
        console.warn(`⚠️ Node ${nodeId} already exists, replacing it`);
        modifiedScenario[nodeId] = nodeData;
        changes.updated.push(nodeId);
        console.log(`✅ Replaced existing node: ${nodeId}`);
      } else {
        // Add the new node
        modifiedScenario[nodeId] = nodeData;
        changes.added.push(nodeId);
        console.log(`✅ Added node: ${nodeId}`);
      }
    });
  }

  // 3. Handle updates
  if (patches.update && typeof patches.update === 'object') {
    Object.entries(patches.update).forEach(([nodeId, updates]) => {
      if (reservedKey(nodeId)) {
        changes.errors.push('Reserved node identity in patch');
        return;
      }
      const node = Object.prototype.hasOwnProperty.call(modifiedScenario, nodeId) ? modifiedScenario[nodeId] : undefined;

      if (!node || typeof node !== 'object') {
        // This is common when removing multiple sequential nodes - adjacent nodes may already be removed
        // Treat as warning, not error
        const warning = `Node ${nodeId} not found or invalid (cannot update) - likely already removed`;
        changes.warnings.push(warning);
        console.warn(`⚠️ ${warning}`);
        return;
      }

      // Handle dot-path field updates
      if (updates.set && typeof updates.set === 'object') {
        Object.entries(updates.set).forEach(([path, value]) => {
          setValueAtPath(node, path, value, nodeId);
          console.log(`✅ Updated ${nodeId}.${path}`);
        });
      }

      // Handle link updates (nextNodes/prevNodes)
      if (updates.links && typeof updates.links === 'object') {
        if (updates.links.nextNodes) {
          node.nextNodes = updates.links.nextNodes;
          console.log(`✅ Updated ${nodeId}.nextNodes`);
        }
        if (updates.links.prevNodes) {
          node.prevNodes = updates.links.prevNodes;
          console.log(`✅ Updated ${nodeId}.prevNodes`);
        }
      }

      changes.updated.push(nodeId);
    });
  }

  console.log('Patch application summary:', {
    added: changes.added.length,
    updated: changes.updated.length,
    removed: changes.removed.length,
    errors: changes.errors.length,
    warnings: changes.warnings.length,
  });

  if (changes.errors.length > 0) {
    console.warn('⚠️ Patch application completed with errors:', changes.errors);
  }

  if (changes.warnings.length > 0) {
    console.warn('⚠️ Patch application warnings (non-critical):', changes.warnings);
  }

  return {
    scenario: modifiedScenario,
    changes,
    success: changes.errors.length === 0, // Only errors count as failures, not warnings
  };
}

/**
 * Utility: Validate that a scenario has proper structure after patching
 */
export function validateScenarioStructure(scenario) {
  const errors = [];
  const warnings = [];

  // Check for start and finish nodes
  const nodes = Object.values(scenario);
  const startNodes = nodes.filter(n => n.type === 'StartNode');
  const finishNodes = nodes.filter(n => n.type === 'FinishNode');

  if (startNodes.length === 0) {
    errors.push('Missing StartNode');
  } else if (startNodes.length > 1) {
    errors.push('Multiple StartNodes found');
  }

  if (finishNodes.length === 0) {
    errors.push('Missing FinishNode');
  } else if (finishNodes.length > 1) {
    errors.push('Multiple FinishNodes found');
  }

  // Check bidirectional links
  Object.entries(scenario).forEach(([nodeId, node]) => {
    // Check nextNodes references exist and have back-references
    if (node.nextNodes) {
      node.nextNodes.forEach(nextId => {
        const nextNode = scenario[nextId];
        if (!nextNode) {
          errors.push(`Node ${nodeId} references non-existent nextNode ${nextId}`);
        } else if (!nextNode.prevNodes || !nextNode.prevNodes.includes(nodeId)) {
          errors.push(`Node ${nodeId} → ${nextId} missing back-reference (prevNodes)`);
        }
      });
    }

    // Check prevNodes references exist and have forward-references
    if (node.prevNodes) {
      node.prevNodes.forEach(prevId => {
        const prevNode = scenario[prevId];
        if (!prevNode) {
          errors.push(`Node ${nodeId} references non-existent prevNode ${prevId}`);
        } else if (!prevNode.nextNodes || !prevNode.nextNodes.includes(nodeId)) {
          errors.push(`Node ${nodeId} ← ${prevId} missing forward-reference (nextNodes)`);
        }
      });
    }

    // Check for orphaned nodes (except StartNode)
    if (node.type !== 'StartNode') {
      if (!node.prevNodes || node.prevNodes.length === 0) {
        warnings.push(`Node ${nodeId} has no prevNodes (orphaned)`);
      }
    }

    // Check for dead-end nodes (except FinishNode)
    if (node.type !== 'FinishNode') {
      if (!node.nextNodes || node.nextNodes.length === 0) {
        warnings.push(`Node ${nodeId} has no nextNodes (dead end)`);
      }
    }
  });

  return {
    isValid: errors.length === 0,
    errors,
    warnings,
  };
}

/**
 * Utility: Auto-repair broken bidirectional links in a scenario
 * This function fixes scenarios where links are inconsistent between nextNodes and prevNodes
 *
 * @param {Object} scenario - The scenario with potentially broken links
 * @param {Object} options - Repair options
 * @param {boolean} options.removeOrphans - Remove nodes that can't be reached from StartNode
 * @param {boolean} options.removeDeadEnds - Remove nodes that don't lead to FinishNode (except special nodes)
 * @returns {Object} - { scenario: repairedScenario, repairs: [...], removed: [...] }
 */
export function repairScenarioBidirectionalLinks(scenario, options = {}) {
  const { removeOrphans = false, removeDeadEnds = false } = options;

  // Deep clone to avoid mutating original
  const repairedScenario = JSON.parse(JSON.stringify(scenario));
  const repairs = [];
  const removed = [];

  // Step 1: Build a complete map of all relationships
  const forwardLinks = {}; // nodeId -> Set of nextNode ids
  const backwardLinks = {}; // nodeId -> Set of prevNode ids

  Object.entries(repairedScenario).forEach(([nodeId, node]) => {
    forwardLinks[nodeId] = new Set(node.nextNodes || []);
    backwardLinks[nodeId] = new Set(node.prevNodes || []);
  });

  // Step 2: Synchronize bidirectional links
  // For each forward link A → B, ensure B has backward link B ← A
  Object.entries(repairedScenario).forEach(([nodeId, node]) => {
    if (node.nextNodes) {
      node.nextNodes.forEach(nextId => {
        if (repairedScenario[nextId]) {
          if (!backwardLinks[nextId].has(nodeId)) {
            backwardLinks[nextId].add(nodeId);
            repairs.push(`Added missing prevNode: ${nextId} ← ${nodeId}`);
            console.log(`🔧 Repair: Added ${nodeId} to ${nextId}.prevNodes`);
          }
        } else {
          // Next node doesn't exist - remove the reference
          forwardLinks[nodeId].delete(nextId);
          repairs.push(`Removed reference to non-existent node: ${nodeId} → ${nextId}`);
          console.log(`🔧 Repair: Removed invalid nextNode ${nextId} from ${nodeId}`);
        }
      });
    }

    if (node.prevNodes) {
      node.prevNodes.forEach(prevId => {
        if (repairedScenario[prevId]) {
          if (!forwardLinks[prevId].has(nodeId)) {
            forwardLinks[prevId].add(nodeId);
            repairs.push(`Added missing nextNode: ${prevId} → ${nodeId}`);
            console.log(`🔧 Repair: Added ${nodeId} to ${prevId}.nextNodes`);
          }
        } else {
          // Prev node doesn't exist - remove the reference
          backwardLinks[nodeId].delete(prevId);
          repairs.push(`Removed reference to non-existent node: ${nodeId} ← ${prevId}`);
          console.log(`🔧 Repair: Removed invalid prevNode ${prevId} from ${nodeId}`);
        }
      });
    }
  });

  // Step 3: Apply the synchronized links back to the scenario
  Object.keys(repairedScenario).forEach(nodeId => {
    repairedScenario[nodeId].nextNodes = Array.from(forwardLinks[nodeId] || []);
    repairedScenario[nodeId].prevNodes = Array.from(backwardLinks[nodeId] || []);
  });

  // Step 4: Optional - Remove orphaned nodes
  if (removeOrphans) {
    const startNodes = Object.values(repairedScenario).filter(n => n.type === 'StartNode');
    if (startNodes.length === 1) {
      const reachableNodes = new Set();
      const queue = [startNodes[0].id];

      while (queue.length > 0) {
        const nodeId = queue.shift();
        if (reachableNodes.has(nodeId)) continue;
        reachableNodes.add(nodeId);

        const node = repairedScenario[nodeId];
        if (node && node.nextNodes) {
          node.nextNodes.forEach(nextId => {
            if (!reachableNodes.has(nextId)) {
              queue.push(nextId);
            }
          });
        }
      }

      // Remove unreachable nodes
      Object.keys(repairedScenario).forEach(nodeId => {
        if (!reachableNodes.has(nodeId)) {
          delete repairedScenario[nodeId];
          removed.push(nodeId);
          console.log(`🗑️ Removed orphaned node: ${nodeId}`);
        }
      });
    }
  }

  // Step 5: Optional - Remove dead-end nodes (that don't lead to FinishNode)
  if (removeDeadEnds) {
    const finishNodes = Object.values(repairedScenario).filter(n => n.type === 'FinishNode');
    if (finishNodes.length === 1) {
      const pathToFinish = new Set();
      const queue = [finishNodes[0].id];

      while (queue.length > 0) {
        const nodeId = queue.shift();
        if (pathToFinish.has(nodeId)) continue;
        pathToFinish.add(nodeId);

        const node = repairedScenario[nodeId];
        if (node && node.prevNodes) {
          node.prevNodes.forEach(prevId => {
            if (!pathToFinish.has(prevId)) {
              queue.push(prevId);
            }
          });
        }
      }

      // Remove nodes that don't lead to finish
      Object.keys(repairedScenario).forEach(nodeId => {
        const node = repairedScenario[nodeId];
        // Don't remove IfClose or IfCondition nodes as they might be structural
        if (!pathToFinish.has(nodeId) &&
            node.type !== 'IfCloseNode' &&
            node.type !== 'IfConditionNode') {
          delete repairedScenario[nodeId];
          removed.push(nodeId);
          console.log(`🗑️ Removed dead-end node: ${nodeId}`);
        }
      });
    }
  }

  console.log(`✅ Repair complete: ${repairs.length} fixes, ${removed.length} nodes removed`);

  return {
    scenario: repairedScenario,
    repairs,
    removed,
    repairCount: repairs.length,
  };
}
